import { z } from 'zod';
import { SummaryLengthSchema } from './link-preview.js';
import { isHttpProtocol } from './utils/http.js';

const VideoUrlSchema = z
  .string()
  .url('Invalid URL format for video summarization')
  .superRefine((value, ctx) => {
    try {
      const parsed = new URL(value);
      if (!isHttpProtocol(parsed.protocol)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Only HTTP and HTTPS video URLs are supported',
        });
      }
    } catch {
      ctx.addIssue({ code: 'custom', message: 'Invalid URL format for video summarization' });
    }
  });

// Optional metadata that helps the video summarizer contextualize the clip (tweet caption, transcript, etc.).
export const VideoSummaryContextSchema = z
  .object({
    title: z.string().trim().min(1).max(240).optional(),
    description: z.string().trim().min(1).max(2e3).optional(),
    transcript: z.string().trim().min(1).max(2e4).optional(),
    text: z.string().trim().min(1).max(4000).optional(),
    authorHandle: z.string().trim().min(1).max(120).optional(),
    source: z.enum(['link', 'tweet']).optional(),
  })
  .optional();

// Normalized facts about the video file so the API route can fast-fail before hitting Gemini.
export const VideoSummaryMetadataSchema = z
  .object({
    durationSeconds: z.number().int().min(1).max(600).optional(),
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(50 * 2 ** 20)
      .optional(),
    contentType: z.string().trim().min(3).max(120).optional(),
    posterUrl: z.string().trim().url('Poster URL must be a valid http(s) URL').optional(),
  })
  .optional();

// Combined request payload exported to every client that calls /api/video/summarize.
export const VideoSummaryRequestSchema = z.object({
  url: VideoUrlSchema,
  summaryLength: SummaryLengthSchema.optional(),
  context: VideoSummaryContextSchema,
  metadata: VideoSummaryMetadataSchema,
  // Used to differentiate analytics later.
  origin: z.enum(['link', 'tweet']).default('link'),
});

export type VideoSummaryRequest = z.infer<typeof VideoSummaryRequestSchema>;
